/* eslint-disable @typescript-eslint/no-explicit-any */
import { openDB, IDBPDatabase } from 'idb';

interface QueuedAction {
  id?: number;
  type: string;
  url: string;
  method: string;
  body?: any;
  headers?: Record<string, string>;
  timestamp: number;
  retries: number;
}

const DB_NAME = 'goodfud-offline';
const STORE_NAME = 'action-queue';
const DB_VERSION = 1;

let dbPromise: Promise<IDBPDatabase> | null = null;

function getDB() {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
        }
      },
    });
  }
  return dbPromise;
}

export async function enqueueAction(action: Omit<QueuedAction, 'id' | 'timestamp' | 'retries'>): Promise<void> {
  const db = await getDB();
  await db.add(STORE_NAME, {
    ...action,
    timestamp: Date.now(),
    retries: 0,
  });
}

export async function getQueuedActions(): Promise<QueuedAction[]> {
  const db = await getDB();
  return db.getAll(STORE_NAME);
}

export async function removeAction(id: number): Promise<void> {
  const db = await getDB();
  await db.delete(STORE_NAME, id);
}

export async function clearQueue(): Promise<void> {
  const db = await getDB();
  await db.clear(STORE_NAME);
}

export async function getQueueSize(): Promise<number> {
  const db = await getDB();
  return db.count(STORE_NAME);
}

export async function processQueue(): Promise<{ success: number; failed: number }> {
  const actions = await getQueuedActions();
  let success = 0;
  let failed = 0;

  for (const action of actions) {
    try {
      const token = localStorage.getItem('goodfud_token');
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...action.headers,
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(action.url, {
        method: action.method,
        headers,
        body: action.body ? JSON.stringify(action.body) : undefined,
      });

      if (response.ok) {
        await removeAction(action.id!);
        success++;
      } else if (response.status >= 400 && response.status < 500) {
        // Client error - remove from queue (won't succeed on retry)
        await removeAction(action.id!);
        failed++;
      } else {
        failed++;
      }
    } catch {
      failed++;
    }
  }

  return { success, failed };
}
